import type { MetaFunction, LoaderFunctionArgs } from 'react-router'
import { CONTENT_PAGE_HEADERS } from "~/lib/cache.server"
import { useLoaderData, Link } from 'react-router'
import { Navbar } from '~/components/navbar'
import { Footer } from '~/components/footer'
import { BreadcrumbSchema, StructuredData, FAQSchema } from '~/components/structured-data'
import { HowToSchema } from '~/components/seo-content'
import { ArrowRight, ArrowLeft, CheckCircle } from 'lucide-react'

const GUIDES: Record<string, { title: string; metaDesc: string; range: string; avg: string; rows: Array<{ service: string; price: string }>; tips: string[] }> = {
  plombier: {
    title: 'Combien coûte un plombier en 2026 ?',
    metaDesc: 'Prix plombier 2026 : tarifs moyens pour dépannage, installation et réparation. De 80 à 500 € selon le type de travaux.',
    range: '80 — 500 €', avg: '210 €',
    rows: [
      { service: 'Dépannage fuite simple', price: '80 — 150 €' },
      { service: 'Débouchage canalisation', price: '100 — 300 €' },
      { service: 'Remplacement robinet', price: '80 — 200 €' },
      { service: 'Installation WC', price: '200 — 500 €' },
      { service: 'Installation chauffe-eau', price: '300 — 800 €' },
      { service: 'Remplacement ballon eau chaude', price: '400 — 1 200 €' },
      { service: 'Déplacement + diagnostic', price: '50 — 100 €' },
    ],
    tips: [
      'Demandez toujours un devis écrit avant le début des travaux',
      'Vérifiez que le plombier est assuré (RC Pro + décennale)',
      'Comparez au moins 3 devis pour le même type de travaux',
      'Méfiance envers les tarifs très inférieurs au marché',
      'Privilégiez un plombier proche de chez vous pour limiter les frais de déplacement',
    ],
  },
  electricien: {
    title: 'Prix d\'un electricien en 2026',
    metaDesc: 'Tarifs électricien 2026 : mise aux normes, installation, dépannage. De 150 à 1 500 € selon les travaux.',
    range: '150 — 1 500 €', avg: '450 €',
    rows: [
      { service: 'Dépannage électrique', price: '80 — 200 €' },
      { service: 'Installation prise/interrupteur', price: '50 — 120 €' },
      { service: 'Mise aux normes tableau', price: '600 — 1 500 €' },
      { service: 'Installation éclairage', price: '100 — 300 €' },
      { service: 'Réfection installation complète', price: '3 000 — 8 000 €' },
    ],
    tips: [
      'La mise aux normes NF C 15-100 est obligatoire en cas de rénovation importante',
      'Privilégiez un électricien certifié RGE pour bénéficier d\'aides',
      'Demandez un diagnostic électrique avant d\'acheter un logement ancien',
    ],
  },
  peinture: {
    title: 'Prix peinture au m2 en 2026',
    metaDesc: 'Coût peinture 2026 : tarifs au m2 pour peinture intérieure et extérieure. De 20 à 45 €/m2 pose comprise.',
    range: '20 — 45 €/m2', avg: '30 €/m2',
    rows: [
      { service: 'Peinture murs (intérieur)', price: '20 — 35 €/m2' },
      { service: 'Peinture plafond', price: '25 — 40 €/m2' },
      { service: 'Peinture facade (extérieur)', price: '30 — 60 €/m2' },
      { service: 'Pose papier peint', price: '25 — 50 €/m2' },
      { service: 'Enduit + peinture', price: '35 — 55 €/m2' },
      { service: 'Laque sur boiseries', price: '30 — 50 €/ml' },
    ],
    tips: [
      'Le prix inclut généralement la préparation des surfaces, la peinture et la main d\'oeuvre',
      'Comptez 10-15% de plus pour une peinture de qualité supérieure',
      'Pour une pièce de 20 m2, comptez environ 600-900 € tout compris',
    ],
  },
  'renovation-salle-de-bain': {
    title: 'Coût rénovation salle de bain en 2026',
    metaDesc: 'Budget salle de bain 2026 : 3000-15000 € selon l\'ampleur. Tarifs détaillés pour chaque poste.',
    range: '3 000 — 15 000 €', avg: '7 500 €',
    rows: [
      { service: 'Rénovation légère (peinture, accessoires)', price: '1 500 — 3 000 €' },
      { service: 'Remplacement baignoire par douche', price: '2 000 — 5 000 €' },
      { service: 'Rénovation complète (standard)', price: '5 000 — 10 000 €' },
      { service: 'Rénovation haut de gamme', price: '10 000 — 20 000 €' },
      { service: 'Carrelage sol + murs', price: '40 — 80 €/m2' },
      { service: 'Meuble vasque + pose', price: '300 — 1 500 €' },
    ],
    tips: [
      'Prévoyez 10-15% du budget total pour les imprévus',
      'L\'étanchéité est le poste à ne pas négliger',
      'Un bon éclairage transforme une salle de bain moyenne en espace premium',
    ],
  },
  'renovation-cuisine': {
    title: 'Coût rénovation cuisine en 2026',
    metaDesc: 'Budget rénovation cuisine 2026 : 5000-25000 €. Meubles, électroménager, plan de travail — tous les prix.',
    range: '5 000 — 25 000 €', avg: '12 000 €',
    rows: [
      { service: 'Rafraîchissement (peinture, crédence)', price: '2 000 — 5 000 €' },
      { service: 'Cuisine équipée entrée de gamme', price: '5 000 — 10 000 €' },
      { service: 'Cuisine équipée milieu de gamme', price: '10 000 — 18 000 €' },
      { service: 'Cuisine sur mesure haut de gamme', price: '18 000 — 35 000 €' },
      { service: 'Plan de travail (granit/quartz)', price: '200 — 500 €/ml' },
      { service: 'Électroménager encastrable', price: '1 500 — 5 000 €' },
    ],
    tips: [
      'Le triangle d\'activité (évier-plaque-frigo) est la clé d\'une cuisine ergonomique',
      'Prévoyez suffisamment de prises électriques',
      'Les cuisinistes indépendants sont souvent 20-30% moins chers que les grandes enseignes',
    ],
  },
  toiture: {
    title: 'Prix réparation et rénovation toiture 2026',
    metaDesc: 'Tarifs toiture 2026 : réparation 500-5000 €, rénovation complète 10000-30000 €. Guide complet.',
    range: '500 — 5 000 €', avg: '2 500 €',
    rows: [
      { service: 'Réparation tuiles/ardoises', price: '200 — 800 €' },
      { service: 'Traitement mousse/hydrofuge', price: '15 — 30 €/m2' },
      { service: 'Réparation gouttières', price: '150 — 500 €' },
      { service: 'Réfection partielle toiture', price: '1 000 — 5 000 €' },
      { service: 'Rénovation complète toiture', price: '10 000 — 30 000 €' },
      { service: 'Isolation sous toiture', price: '50 — 120 €/m2' },
    ],
    tips: [
      'Faites inspecter votre toiture tous les 5 ans',
      'La garantie décennale est obligatoire pour les travaux de toiture',
      'L\'isolation de la toiture est le poste le plus rentable en rénovation énergétique',
    ],
  },
  serrurerie: {
    title: 'Combien coûte un serrurier en 2026 ? Tarifs et prix',
    metaDesc: 'Combien coûte un serrurier ? Ouverture de porte 80-250 €, changement serrure 150-400 €, blindage porte 800-2500 €. Tarifs serrurier 2026.',
    range: '80 — 400 €', avg: '180 €',
    rows: [
      { service: 'Ouverture porte claquée', price: '80 — 150 €' },
      { service: 'Ouverture porte fermée à clé', price: '150 — 300 €' },
      { service: 'Changement serrure standard', price: '100 — 250 €' },
      { service: 'Serrure multipoints', price: '200 — 500 €' },
      { service: 'Blindage de porte', price: '800 — 2 500 €' },
      { service: 'Porte blindée complète', price: '1 500 — 4 000 €' },
    ],
    tips: [
      'En urgence, demandez le tarif AVANT l\'intervention',
      'Un serrurier agréé par votre assurance facilite le remboursement',
      'Méfiez-vous des serruriers qui apparaissent en première position sur Google Ads',
    ],
  },
  carrelage: {
    title: 'Prix pose carrelage en 2026',
    metaDesc: 'Tarifs carrelage 2026 : pose sol 25-70 €/m², mural 30-60 €/m². Carreleur professionnel — guide des prix.',
    range: '25 — 70 €/m2', avg: '45 €/m2',
    rows: [
      { service: 'Carrelage sol (pose droite)', price: '25 — 45 €/m2' },
      { service: 'Carrelage sol (pose diagonale)', price: '35 — 55 €/m2' },
      { service: 'Carrelage mural', price: '30 — 60 €/m2' },
      { service: 'Mosaïque', price: '50 — 100 €/m2' },
      { service: 'Dépose ancien carrelage', price: '15 — 30 €/m2' },
      { service: 'Ragréage (sol irrégulier)', price: '15 — 25 €/m2' },
    ],
    tips: [
      'Le prix du carrelage lui-même n\'est pas inclus dans le tarif de pose',
      'Prévoyez 10% de carreaux en plus pour la casse',
      'Les grands formats (60x60+) coûtent plus cher à poser mais le rendu est plus moderne',
    ],
  },
  isolation: {
    title: 'Prix isolation maison en 2026',
    metaDesc: 'Coût isolation 2026 : combles 20-50 €/m², murs ITE 100-200 €/m², ITI 40-80 €/m². Guide complet.',
    range: '20 — 200 €/m2', avg: '60 €/m2',
    rows: [
      { service: 'Isolation combles perdus (soufflage)', price: '20 — 35 €/m2' },
      { service: 'Isolation combles aménagés', price: '40 — 80 €/m2' },
      { service: 'Isolation murs par l\'intérieur (ITI)', price: '40 — 80 €/m2' },
      { service: 'Isolation murs par l\'extérieur (ITE)', price: '100 — 200 €/m2' },
      { service: 'Isolation plancher bas', price: '25 — 60 €/m2' },
      { service: 'Isolation phonique cloison', price: '30 — 60 €/m2' },
    ],
    tips: [
      'Les combles perdus sont le geste le plus rentable (ROI en 2-5 ans)',
      'L\'ITE supprime les ponts thermiques mais coûte 2-3x plus que l\'ITI',
      'Artisan RGE obligatoire pour bénéficier des aides MaPrimeRénov\'',
      'Faites réaliser un audit énergétique avant de choisir la technique',
    ],
  },
  climatisation: {
    title: 'Prix installation climatisation en 2026',
    metaDesc: 'Tarifs climatisation 2026 : split 1 500-3 000 €, multi-split 3 000-6 000 €, PAC air-air 5 000-10 000 €.',
    range: '500 — 5 000 €', avg: '2 500 €',
    rows: [
      { service: 'Climatiseur mobile (fourni-posé)', price: '300 — 800 €' },
      { service: 'Split mural (1 pièce)', price: '1 500 — 3 000 €' },
      { service: 'Multi-split (2-3 pièces)', price: '3 000 — 6 000 €' },
      { service: 'Gainable (maison entière)', price: '5 000 — 12 000 €' },
      { service: 'PAC air-air réversible', price: '5 000 — 10 000 €' },
      { service: 'Entretien annuel obligatoire', price: '100 — 250 €' },
    ],
    tips: [
      'L\'entretien annuel est obligatoire pour les appareils > 2 kg de fluide',
      'Le multi-split est plus économique que plusieurs splits individuels',
      'Privilégiez un installateur certifié RGE QualiPAC pour les aides',
    ],
  },
  // (renovation-salle-de-bain and renovation-cuisine were defined a second
  // time here in the original file. The earlier definitions at lines ~66
  // and ~84 are kept; the duplicates that crashed the vite build have been
  // removed entirely.)
  menuisier: {
    title: 'Prix menuisier en 2026',
    metaDesc: 'Tarifs menuisier 2026 : fenêtres 300-800 €, porte intérieure 150-500 €, placard sur mesure 500-3 000 €.',
    range: '200 — 3 000 €', avg: '800 €',
    rows: [
      { service: 'Fenêtre PVC double vitrage', price: '300 — 600 €' },
      { service: 'Fenêtre bois double vitrage', price: '400 — 800 €' },
      { service: 'Fenêtre alu double vitrage', price: '500 — 1 000 €' },
      { service: 'Porte intérieure (pose)', price: '150 — 500 €' },
      { service: 'Porte d\'entrée (fourni-posé)', price: '800 — 3 000 €' },
      { service: 'Placard / dressing sur mesure', price: '500 — 3 000 €' },
      { service: 'Volet roulant (motorisé)', price: '300 — 800 €' },
    ],
    tips: [
      'Le PVC offre le meilleur rapport qualité/prix pour les fenêtres',
      'Les fenêtres sont éligibles à MaPrimeRénov\' et aux CEE',
      'Un volet roulant motorisé ajoute confort et valeur au bien',
    ],
  },
  maconnerie: {
    title: 'Prix maçonnerie en 2026',
    metaDesc: 'Tarifs maçon 2026 : mur parpaing 50-100 €/m², dalle béton 40-80 €/m², ouverture mur porteur 2 000-5 000 €.',
    range: '40 — 200 €/m2', avg: '80 €/m2',
    rows: [
      { service: 'Mur en parpaing (fourni-posé)', price: '50 — 100 €/m2' },
      { service: 'Dalle béton', price: '40 — 80 €/m2' },
      { service: 'Ouverture mur porteur (IPN)', price: '2 000 — 5 000 €' },
      { service: 'Démolition cloison', price: '20 — 50 €/m2' },
      { service: 'Enduit façade', price: '30 — 80 €/m2' },
      { service: 'Terrassement', price: '30 — 60 €/m3' },
    ],
    tips: [
      'L\'ouverture d\'un mur porteur nécessite une étude structure par un ingénieur',
      'En copropriété, l\'accord de l\'AG est obligatoire pour un mur porteur',
      'Le terrassement est moins cher en hiver (période creuse)',
    ],
  },
  demenagement: {
    title: 'Prix déménagement en 2026',
    metaDesc: 'Coût déménagement 2026 : studio 300-800 €, T3 800-2 000 €, maison 1 500-5 000 € selon distance.',
    range: '300 — 5 000 €', avg: '1 200 €',
    rows: [
      { service: 'Studio / T1 (local)', price: '300 — 800 €' },
      { service: 'T2 (même ville)', price: '500 — 1 200 €' },
      { service: 'T3-T4 (même ville)', price: '800 — 2 000 €' },
      { service: 'T3-T4 (longue distance)', price: '1 500 — 3 500 €' },
      { service: 'Maison (longue distance)', price: '2 500 — 5 000 €' },
      { service: 'Monte-meuble', price: '200 — 800 €' },
      { service: 'Garde-meuble (par mois)', price: '50 — 200 €/mois' },
    ],
    tips: [
      'Déménagez en milieu de mois ou en semaine — tarifs 20-30% moins chers',
      'Faites l\'inventaire précis de vos biens pour des devis comparables',
      'Vérifiez l\'assurance du déménageur (responsabilité civile + ad valorem)',
    ],
  },
  jardinage: {
    title: 'Prix jardinier / paysagiste en 2026',
    metaDesc: 'Tarifs jardinage 2026 : tonte 30-50 €/h, élagage 200-1 500 €, création jardin 2 000-10 000 €.',
    range: '30 — 80 €/h', avg: '45 €/h',
    rows: [
      { service: 'Tonte pelouse (heure)', price: '30 — 50 €/h' },
      { service: 'Taille de haies (heure)', price: '35 — 55 €/h' },
      { service: 'Élagage arbre', price: '200 — 1 500 €' },
      { service: 'Abattage arbre', price: '300 — 2 000 €' },
      { service: 'Création de massifs', price: '50 — 150 €/m2' },
      { service: 'Pose gazon en plaques', price: '15 — 30 €/m2' },
      { service: 'Clôture (grillage rigide)', price: '40 — 80 €/ml' },
    ],
    tips: [
      'Le jardinage à domicile bénéficie du crédit d\'impôt 50% (max 5 000 €/an)',
      'Demandez un devis détaillé avec évacuation des déchets incluse',
      'L\'élagage est interdit entre le 15 mars et le 31 juillet (période de nidification)',
    ],
  },
  nettoyage: {
    title: 'Prix nettoyage / ménage en 2026',
    metaDesc: 'Tarifs nettoyage 2026 : ménage 20-35 €/h, fin de chantier 200-800 €, nettoyage copropriété 300-1 500 €.',
    range: '20 — 50 €/h', avg: '30 €/h',
    rows: [
      { service: 'Ménage régulier (heure)', price: '20 — 35 €/h' },
      { service: 'Nettoyage fin de chantier', price: '200 — 800 €' },
      { service: 'Nettoyage vitres (heure)', price: '25 — 45 €/h' },
      { service: 'Shampoing moquette/tapis', price: '3 — 8 €/m2' },
      { service: 'Nettoyage copropriété (mensuel)', price: '300 — 1 500 €/mois' },
      { service: 'Débarras / vide-maison', price: '300 — 2 000 €' },
    ],
    tips: [
      'Le ménage à domicile bénéficie du crédit d\'impôt 50%',
      'Pour un nettoyage fin de chantier, attendez 48h après les travaux',
      'Vérifiez l\'assurance du prestataire (casse, vol)',
    ],
  },
  'pompe-a-chaleur': {
    title: 'Prix pompe à chaleur (PAC) en 2026',
    metaDesc: 'Coût PAC 2026 : air-eau 8 000-16 000 €, air-air 5 000-10 000 €, géothermie 15 000-25 000 €. Aides incluses.',
    range: '5 000 — 25 000 €', avg: '12 000 €',
    rows: [
      { service: 'PAC air-eau (8-12 kW)', price: '8 000 — 16 000 €' },
      { service: 'PAC air-air réversible', price: '5 000 — 10 000 €' },
      { service: 'PAC géothermique', price: '15 000 — 25 000 €' },
      { service: 'Ballon thermodynamique', price: '2 000 — 4 000 €' },
      { service: 'Entretien annuel PAC', price: '150 — 300 €' },
    ],
    tips: [
      'MaPrimeRénov\' : jusqu\'à 5 000 € d\'aide pour une PAC air-eau',
      'Artisan RGE QualiPAC obligatoire pour les aides',
      'Le COP moyen d\'une PAC air-eau est de 3-4 (1 kWh consommé = 3-4 kWh de chaleur)',
      'Faites réaliser un bilan thermique avant installation',
    ],
  },
  'chaudiere-gaz': {
    title: 'Prix chaudière gaz condensation en 2026',
    metaDesc: 'Tarifs chaudière gaz 2026 : murale 2 000-4 000 €, sol 3 000-6 000 €. Attention : plus aucune aide depuis 2025.',
    range: '2 000 — 6 000 €', avg: '3 500 €',
    rows: [
      { service: 'Chaudière murale condensation', price: '2 000 — 4 000 €' },
      { service: 'Chaudière sol condensation', price: '3 000 — 6 000 €' },
      { service: 'Chaudière micro-cogénération', price: '10 000 — 15 000 €' },
      { service: 'Entretien annuel (obligatoire)', price: '100 — 200 €' },
      { service: 'Ramonage conduit', price: '50 — 100 €' },
    ],
    tips: [
      'Plus aucune aide pour les chaudières gaz depuis janvier 2025',
      'Interdit dans les constructions neuves (RE2020)',
      'Envisagez une PAC air-eau comme alternative — éligible aux aides',
    ],
  },
  'panneaux-solaires': {
    title: 'Prix panneaux solaires en 2026',
    metaDesc: 'Coût panneaux solaires 2026 : 3 kWc 7 000-10 000 €, 6 kWc 12 000-16 000 €. Rentabilité en 8-10 ans.',
    range: '7 000 — 22 000 €', avg: '14 000 €',
    rows: [
      { service: '3 kWc (8-10 panneaux)', price: '7 000 — 10 000 €' },
      { service: '6 kWc (16-18 panneaux)', price: '12 000 — 16 000 €' },
      { service: '9 kWc (24-26 panneaux)', price: '16 000 — 22 000 €' },
      { service: 'Batterie de stockage', price: '3 000 — 8 000 €' },
      { service: 'Onduleur (remplacement)', price: '1 000 — 2 500 €' },
    ],
    tips: [
      'Prime autoconsommation : 220-350 €/kWc versée sur 5 ans',
      'TVA 10% pour installations ≤ 3 kWc',
      'Orientation sud avec inclinaison 30° = production optimale',
      'Installateur RGE QualiPV obligatoire pour les aides',
    ],
  },
  parquet: {
    title: 'Prix pose parquet en 2026',
    metaDesc: 'Tarifs parquet 2026 : stratifié 15-40 €/m², contrecollé 30-80 €/m², massif 60-150 €/m². Pose comprise.',
    range: '15 — 150 €/m2', avg: '55 €/m2',
    rows: [
      { service: 'Stratifié (clipsable)', price: '15 — 40 €/m2' },
      { service: 'Parquet contrecollé', price: '30 — 80 €/m2' },
      { service: 'Parquet massif (collé)', price: '60 — 150 €/m2' },
      { service: 'Ponçage + vitrification', price: '25 — 45 €/m2' },
      { service: 'Pose escalier bois', price: '1 000 — 5 000 €' },
      { service: 'Plinthes (fourni-posé)', price: '8 — 20 €/ml' },
    ],
    tips: [
      'Le contrecollé offre le meilleur rapport qualité/prix/esthétique',
      'Prévoyez 10% de matériau en plus pour les découpes',
      'Laissez le parquet s\'acclimater 48h dans la pièce avant la pose',
    ],
  },
  'salle-de-bain-pmr': {
    title: 'Prix salle de bain PMR en 2026',
    metaDesc: 'Aménagement SDB PMR 2026 : douche accessible 3 000-6 000 €, salle de bain complète 5 000-12 000 €. Aides 70%.',
    range: '5 000 — 12 000 €', avg: '8 000 €',
    rows: [
      { service: 'Douche italienne PMR', price: '3 000 — 6 000 €' },
      { service: 'WC surélevé + barres', price: '500 — 1 500 €' },
      { service: 'Lavabo PMR suspendu', price: '400 — 1 000 €' },
      { service: 'Sol antidérapant', price: '30 — 60 €/m2' },
      { service: 'Barres de maintien (pièce)', price: '50 — 200 €' },
      { service: 'Siège rabattable douche', price: '100 — 400 €' },
    ],
    tips: [
      'MaPrimeAdapt\' : jusqu\'à 70% du coût (max 22 000 €) pour les +70 ans',
      'Crédit d\'impôt 25% pour l\'adaptation du logement',
      'Consultez un ergothérapeute pour cibler les vrais besoins',
    ],
  },
  ravalement: {
    title: 'Prix ravalement de façade en 2026',
    metaDesc: 'Coût ravalement 2026 : nettoyage 15-30 €/m², peinture 25-50 €/m², enduit 40-100 €/m², ITE 100-200 €/m².',
    range: '15 — 200 €/m2', avg: '60 €/m2',
    rows: [
      { service: 'Nettoyage haute pression', price: '15 — 30 €/m2' },
      { service: 'Peinture de façade', price: '25 — 50 €/m2' },
      { service: 'Enduit monocouche', price: '40 — 80 €/m2' },
      { service: 'Enduit traditionnel (2 couches)', price: '50 — 100 €/m2' },
      { service: 'ITE (isolation par l\'extérieur)', price: '100 — 200 €/m2' },
      { service: 'Traitement hydrofuge', price: '10 — 25 €/m2' },
      { service: 'Échafaudage', price: '15 — 30 €/m2' },
    ],
    tips: [
      'Obligatoire tous les 10 ans dans certaines communes (Paris, Lyon...)',
      'L\'ITE permet de cumuler ravalement + isolation — MaPrimeRénov\' jusqu\'à 75 €/m²',
      'Meilleure période : printemps ou automne (températures 5-25°C)',
    ],
  },
  piscine: {
    title: 'Prix construction piscine en 2026',
    metaDesc: 'Coût piscine 2026 : coque 15 000-30 000 €, béton 25 000-60 000 €, hors-sol 1 000-8 000 €.',
    range: '1 000 — 60 000 €', avg: '25 000 €',
    rows: [
      { service: 'Piscine hors-sol', price: '1 000 — 8 000 €' },
      { service: 'Piscine coque polyester', price: '15 000 — 30 000 €' },
      { service: 'Piscine béton (maçonnée)', price: '25 000 — 60 000 €' },
      { service: 'Piscine naturelle', price: '30 000 — 50 000 €' },
      { service: 'Terrassement (8x4m)', price: '2 500 — 5 500 €' },
      { service: 'Local technique complet', price: '3 000 — 6 000 €' },
      { service: 'Entretien annuel', price: '500 — 1 500 €/an' },
    ],
    tips: [
      'Déclaration préalable obligatoire (piscine > 10 m²)',
      'La piscine augmente la taxe foncière (+ 10-30%)',
      'Prévoyez 15-20% du budget pour le terrassement',
    ],
  },
  'borne-recharge': {
    title: 'Prix borne de recharge électrique en 2026',
    metaDesc: 'Coût borne recharge 2026 : prise renforcée 300-500 €, wallbox 7 kW 1 000-1 800 €, 22 kW 2 000-3 500 €.',
    range: '300 — 3 500 €', avg: '1 500 €',
    rows: [
      { service: 'Prise renforcée (Green\'Up)', price: '300 — 500 €' },
      { service: 'Wallbox 7,4 kW (monophasé)', price: '1 000 — 1 800 €' },
      { service: 'Wallbox 11 kW (triphasé)', price: '1 500 — 2 500 €' },
      { service: 'Wallbox 22 kW (triphasé)', price: '2 000 — 3 500 €' },
      { service: 'Mise à jour tableau électrique', price: '200 — 500 €' },
    ],
    tips: [
      'Crédit d\'impôt 75% plafonné à 300 €',
      'Installateur IRVE certifié obligatoire pour les aides',
      '7,4 kW = meilleur rapport qualité/prix pour 90% des usages',
    ],
  },
  vmc: {
    title: 'Prix VMC en 2026',
    metaDesc: 'Tarifs VMC 2026 : simple flux 500-1 500 €, double flux 2 000-5 000 €, hygro B 700-2 000 €. Installation comprise.',
    range: '500 — 5 000 €', avg: '1 500 €',
    rows: [
      { service: 'VMC simple flux autoréglable', price: '500 — 1 000 €' },
      { service: 'VMC simple flux hygro B', price: '700 — 2 000 €' },
      { service: 'VMC double flux', price: '2 000 — 5 000 €' },
      { service: 'VMC double flux thermodynamique', price: '4 000 — 8 000 €' },
      { service: 'Entretien / nettoyage gaines', price: '100 — 300 €' },
    ],
    tips: [
      'La VMC est obligatoire dans tout logement (arrêté du 24 mars 1982)',
      'L\'hygro B est le meilleur rapport qualité/prix en rénovation',
      'La double flux récupère 70-90% de la chaleur de l\'air extrait',
    ],
  },
  'chauffe-eau': {
    title: 'Prix chauffe-eau en 2026',
    metaDesc: 'Tarifs chauffe-eau 2026 : électrique 500-1 200 €, thermodynamique 2 000-4 000 €, solaire 4 000-7 000 €.',
    range: '500 — 7 000 €', avg: '1 500 €',
    rows: [
      { service: 'Chauffe-eau électrique (100-200L)', price: '500 — 1 200 €' },
      { service: 'Chauffe-eau thermodynamique', price: '2 000 — 4 000 €' },
      { service: 'Chauffe-eau solaire (CESI)', price: '4 000 — 7 000 €' },
      { service: 'Chauffe-eau gaz instantané', price: '600 — 1 500 €' },
      { service: 'Détartrage chauffe-eau', price: '100 — 250 €' },
    ],
    tips: [
      'Le thermodynamique consomme 3x moins que l\'électrique classique',
      'MaPrimeRénov\' : jusqu\'à 1 200 € pour un chauffe-eau solaire',
      'Détartrez tous les 2-3 ans pour maintenir le rendement',
    ],
  },
}

const FALLBACK = {
  title: 'Guide des prix', metaDesc: 'Guide des prix travaux.', range: '--', avg: '--',
  rows: [{ service: 'Guide en cours de rédaction', price: 'Bientôt disponible' }],
  tips: ['Demandez plusieurs devis pour comparer les prix', 'Vérifiez les assurances du professionnel'],
}

export async function loader({ params }: LoaderFunctionArgs) {
  const guide = GUIDES[params.slug ?? ''] ?? FALLBACK
  return { slug: params.slug!, ...guide }
}

export function headers() { return CONTENT_PAGE_HEADERS }

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  const range = data?.range ? `${data.range}` : ''
  // Lift CTR: keep curiosity title from GUIDES dict + append concrete price range
  // when available + always end with strong CTA. Description retains the dict
  // metaDesc but adds tracker-friendly benefit clause.
  return [
    { title: range
      ? `${data?.title ?? 'Guide prix'} ${range} | Devis gratuit 48h`
      : `${data?.title ?? 'Guide prix'} | Devis gratuit 48h` },
    { name: 'description', content: `${data?.metaDesc ?? ''} ✓ Grille détaillée ✓ 3 devis gratuits sous 48h ✓ Artisans SIRET vérifiés.` },
    { tagName: 'link', rel: 'canonical', href: `https://www.afficheservices.fr/guide-prix/${data?.slug}` },
  ]
}

export default function GuidePrixDetail() {
  const guide = useLoaderData<typeof loader>()

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <BreadcrumbSchema items={[
        { name: 'Accueil', url: 'https://www.afficheservices.fr/' },
        { name: 'Guide des prix', url: 'https://www.afficheservices.fr/guide-prix' },
        { name: guide.title, url: `https://www.afficheservices.fr/guide-prix/${guide.slug}` },
      ]} />
      <StructuredData data={{
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: guide.title,
        description: guide.metaDesc,
        url: `https://www.afficheservices.fr/guide-prix/${guide.slug}`,
        // Article needs author + image + publisher.logo для rich result eligibility.
        author: { '@type': 'Organization', '@id': 'https://www.afficheservices.fr/#org', name: 'AfficheServices' },
        publisher: {
          '@type': 'Organization',
          '@id': 'https://www.afficheservices.fr/#org',
          name: 'AfficheServices',
          url: 'https://www.afficheservices.fr',
          logo: { '@type': 'ImageObject', url: 'https://www.afficheservices.fr/favicon.svg' },
        },
        image: `https://www.afficheservices.fr/api/og?title=${encodeURIComponent(guide.title)}`,
        mainEntityOfPage: { '@type': 'WebPage', '@id': `https://www.afficheservices.fr/guide-prix/${guide.slug}` },
        datePublished: '2026-03-01',
        dateModified: '2026-03-25',
        articleSection: 'Guide prix',
      }} />
      {/* HowTo (steps) — eligible для "Comment économiser" rich result */}
      <HowToSchema
        name={`Comment optimiser le coût d'un ${guide.slug.replace(/-/g, ' ')}`}
        steps={guide.tips.map((tip, i) => ({ title: `Étape ${i + 1}`, detail: tip }))}
      />
      {/* FAQ generated from price rows — eligible для FAQ rich result */}
      <FAQSchema items={guide.rows.map(r => ({
        question: `Combien coûte ${r.service.toLowerCase()} ?`,
        answer: `${r.service} coûte ${r.price} en France. Tarif indicatif TTC pour ${new Date().getFullYear()}, variable selon la région et la complexité.`,
      }))} />

      <div className="mx-auto max-w-3xl px-4 py-12 lg:px-6">
        <Link to="/guide-prix" className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline">
          <ArrowLeft className="h-3.5 w-3.5" /> Tous les guides
        </Link>

        <h1 className="mt-6 text-3xl font-bold text-slate-900">{guide.title}</h1>

        {/* Price summary */}
        <div className="mt-6 grid grid-cols-2 gap-4">
          <div className="rounded-2xl border border-border bg-surface-secondary p-5">
            <p className="text-xs font-medium text-slate-400">Fourchette de prix</p>
            <p className="mt-1 text-2xl font-bold text-slate-900">{guide.range}</p>
          </div>
          <div className="rounded-2xl border border-border bg-primary-50 p-5">
            <p className="text-xs font-medium text-primary">Prix moyen</p>
            <p className="mt-1 text-2xl font-bold text-primary">{guide.avg}</p>
          </div>
        </div>

        {/* Price table */}
        <div className="mt-10">
          <h2 className="text-xl font-semibold text-slate-900">Détail des tarifs</h2>
          <div className="mt-4 overflow-hidden rounded-xl border border-border">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50">
                  <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500">Type de travaux</th>
                  <th className="px-5 py-3 text-right text-xs font-semibold text-slate-500">Prix indicatif</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {guide.rows.map((row) => (
                  <tr key={row.service} className="hover:bg-slate-50">
                    <td className="px-5 py-3.5 text-sm text-slate-900">{row.service}</td>
                    <td className="px-5 py-3.5 text-right text-sm font-semibold text-primary">{row.price}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-2 text-xs text-slate-400">* Prix indicatifs TTC constates en France métropolitaine. Les tarifs peuvent varier selon la region et la complexité.</p>
        </div>

        {/* Tips */}
        <div className="mt-10">
          <h2 className="text-xl font-semibold text-slate-900">Nos conseils</h2>
          <ul className="mt-4 space-y-3">
            {guide.tips.map((tip) => (
              <li key={tip} className="flex gap-3 text-sm text-slate-600">
                <CheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
                {tip}
              </li>
            ))}
          </ul>
        </div>

        {/* CTA */}
        <div className="mt-12 rounded-2xl bg-primary-50 p-8 text-center">
          <h3 className="text-lg font-bold text-slate-900">Obtenez un prix exact pour votre projet</h3>
          <p className="mt-2 text-sm text-slate-500">Comparez les devis de professionnels vérifiés</p>
          <Link to="/demande-devis/1" className="mt-4 inline-flex items-center gap-2 rounded-xl bg-primary px-6 py-3 text-sm font-semibold text-white hover:bg-primary-hover btn-press">
            Demander un devis gratuit <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>

      <Footer />
    </div>
  )
}
